import { ICON_COLOR } from '@zep-us/zus/tokens';
import { AlarmFillIcon, XIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { Button, IconButton } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { useTrialEndCountdownText } from './useTrialEndCountdownText';
export type TrialEndBannerState = 'active' | 'urgent' | 'ended';

/** Figma 29139:3337(>24h) / 29139:3340(<24h) / 29139:3344(=0) */
const STATE_STYLE: Record<TrialEndBannerState, {
  background: string;
  countdown: string;
  icon: string;
}> = {
  active: {
    background: 'bg-blue-50',
    countdown: 'text-link',
    icon: '#0D94FF'
  },
  urgent: {
    background: 'bg-red-50',
    countdown: 'text-destructive',
    icon: '#FF4848'
  },
  ended: {
    background: 'bg-neutral',
    countdown: 'text-assistive',
    icon: '#9E9FAF'
  }
};
export interface TrialEndDiscountBannerProps {
  state: TrialEndBannerState;
  remainingMs: number;
  discountRate: number;
  onDetailClick: () => void;
  onClose: () => void;
}

/**
 * GNB 위 전폭 할인 배너 (PRD 솔루션 6, S6-03/07).
 * `ended` 는 CTA 없이 회색으로만 알리고 X 를 누르면 영구히 사라진다.
 */
export function TrialEndDiscountBanner({
  state,
  remainingMs,
  discountRate,
  onDetailClick,
  onClose
}: TrialEndDiscountBannerProps) {
  const {
    t
  } = useTranslation(['common']);
  const {
    countdown
  } = useTrialEndCountdownText(remainingMs);
  const style = STATE_STYLE[state];
  const title = t('common:trialEndDiscount.banner.title', {
    rate: Math.round(discountRate * 100)
  });
  return <div datatype="trial-end-discount-banner" className={cn('relative flex max-h-[44px] min-h-[44px] w-full items-center justify-center gap-sm overflow-hidden px-md py-xs tablet-sm:max-h-[40px] tablet-sm:min-h-[40px] tablet-sm:px-[24px] tablet-lg:px-[40px]', style.background)} data-sentry-component="TrialEndDiscountBanner" data-sentry-source-file="TrialEndDiscountBanner.tsx">
      <div className="flex min-w-0 flex-1 items-center justify-center gap-xs">
        <p className="truncate text-body-sm-medium text-default max-[560px]:text-body-md-medium">
          {title}
        </p>
        <div className="flex shrink-0 items-center gap-3xs">
          <AlarmFillIcon size="xs" fill={style.icon} data-sentry-element="AlarmFillIcon" data-sentry-source-file="TrialEndDiscountBanner.tsx" />
          <p className={cn('truncate text-body-sm-bold max-[560px]:text-body-md-bold', style.countdown)}>
            {countdown}
          </p>
        </div>
        {state !== 'ended' && <Button variant="contained" intent="gradient" size="3xs" className="hidden h-[18px] shrink-0 rounded-[4px] px-2xs tablet-sm:inline-flex" onClick={onDetailClick}>
            {t('common:trialEndDiscount.banner.cta')}
          </Button>}
      </div>
      <IconButton aria-label={t('common:global.close.button')} variant="transparent" intent="neutral" size="sm" className="absolute right-md justify-end tablet-lg:right-[40px]" onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="TrialEndDiscountBanner.tsx">
        <XIcon size="xs" fill={ICON_COLOR.alternative} data-sentry-element="XIcon" data-sentry-source-file="TrialEndDiscountBanner.tsx" />
      </IconButton>
    </div>;
}